import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Trash2 } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const SECTIONS = { bike: "Bike check", kit: "Kit on bike", question: "Question" };
const STAGES = { both: "Pre & post", pre: "Pre-ride only", post: "Post-ride only" };
const KINDS = { check: "OK / Fault / Not done", question: "Yes / No", text: "Free text" };
export const DEFAULT_STOP = "DO NOT TAKE THIS BIKE. Report the fault to the Fleet Manager.";

const ItemRow = ({ it, onSave, onDelete }) => {
  const patch = (p) => onSave({ ...it, ...p });
  return (
    <div data-testid={`check-item-${it.id}`} className={`flex flex-wrap items-center gap-3 border-b px-5 py-3 last:border-0 ${it.active ? "" : "opacity-50"}`}>
      <div className="min-w-[180px] flex-1">
        <div className="text-sm font-semibold">{it.label}</div>
        <div className="text-xs text-muted-foreground">{SECTIONS[it.section]} · {KINDS[it.kind]}</div>
      </div>
      {it.section === "question" && (
        <Select value={it.kind} onValueChange={(v) => patch({ kind: v })}>
          <SelectTrigger className="h-9 w-[130px]" data-testid={`item-kind-${it.id}`}><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="question">Yes / No</SelectItem><SelectItem value="text">Free text</SelectItem></SelectContent>
        </Select>
      )}
      <Select value={it.stage} onValueChange={(v) => patch({ stage: v })}>
        <SelectTrigger className="h-9 w-[150px]" data-testid={`item-stage-${it.id}`}><SelectValue /></SelectTrigger>
        <SelectContent>{Object.entries(STAGES).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent>
      </Select>
      <label className="flex items-center gap-2 text-xs"><Switch checked={it.mandatory} onCheckedChange={(v) => patch({ mandatory: v })} data-testid={`item-mandatory-${it.id}`} /> Mandatory</label>
      <label className="flex items-center gap-2 text-xs"><Switch checked={it.active} onCheckedChange={(v) => patch({ active: v })} data-testid={`item-active-${it.id}`} /> In use</label>
      {it.kind !== "text" && <label className="flex items-center gap-2 text-xs font-semibold text-red-300"><Switch checked={!!it.stop_ride} onCheckedChange={(v) => patch({ stop_ride: v })} data-testid={`item-stop-${it.id}`} /> Do not ride if fault</label>}
      <button onClick={() => onDelete(it)} data-testid={`item-delete-${it.id}`} className="rounded-full p-2 text-red-400 hover:bg-red-500/10" aria-label="Delete"><Trash2 className="h-4 w-4" /></button>
      {it.stop_ride && it.kind !== "text" && (
        <Input defaultValue={it.stop_message || ""} onBlur={(e) => e.target.value !== (it.stop_message || "") && patch({ stop_message: e.target.value })} placeholder={`Message shown if this fails, e.g. ${DEFAULT_STOP}`}
          className="h-9 w-full border-red-500/40" data-testid={`item-stop-msg-${it.id}`} />
      )}
    </div>
  );
};

export const ChecklistEditor = () => {
  const [items, setItems] = useState([]);
  const [label, setLabel] = useState("");
  const [section, setSection] = useState("bike");

  useEffect(() => { api.get("/check-items").then((r) => setItems(r.data)); }, []);

  const save = async (it) => {
    try {
      const { label: l, section: s, kind, stage, mandatory, active, stop_ride: sr, stop_message: sm } = it;
      const { data } = await api.patch(`/check-items/${it.id}`, { label: l, section: s, kind, stage, mandatory, active, stop_ride: !!sr, stop_message: sm || "" });
      setItems((x) => x.map((y) => (y.id === data.id ? data : y)));
    } catch (e) { toast.error(errMsg(e)); }
  };
  const remove = async (it) => {
    if (!window.confirm(`Remove "${it.label}" from the checklist?`)) return;
    await api.delete(`/check-items/${it.id}`);
    setItems((x) => x.filter((y) => y.id !== it.id));
  };
  const add = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.post("/check-items", { label, section, kind: section === "question" ? "question" : "check", stage: "both", mandatory: true, active: true });
      setItems((x) => [...x, data]);
      setLabel("");
      toast.success("Item added");
    } catch (err) { toast.error(errMsg(err)); }
  };

  return (
    <div className="space-y-4">
      <form onSubmit={add} className="flex flex-wrap gap-3 rounded-2xl border bg-card p-4">
        <Input required placeholder="New check or question, e.g. Phone fully charged?" value={label} onChange={(e) => setLabel(e.target.value)} className="h-11 min-w-[220px] flex-1" data-testid="new-item-label-input" />
        <Select value={section} onValueChange={setSection}>
          <SelectTrigger className="h-11 w-[160px]" data-testid="new-item-section-select"><SelectValue /></SelectTrigger>
          <SelectContent>{Object.entries(SECTIONS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent>
        </Select>
        <button type="submit" data-testid="new-item-add-btn" className="rounded-full bg-primary px-5 text-sm font-bold text-primary-foreground">Add</button>
      </form>
      {Object.entries(SECTIONS).map(([k, v]) => {
        const list = items.filter((i) => i.section === k);
        if (!list.length) return null;
        return (
          <section key={k} className="overflow-hidden rounded-2xl border bg-card">
            <h3 className="border-b px-5 py-3 text-sm font-bold uppercase tracking-widest text-muted-foreground">{v}</h3>
            {list.map((it) => <ItemRow key={it.id} it={it} onSave={save} onDelete={remove} />)}
          </section>
        );
      })}
    </div>
  );
};
